import { useState, useRef, useEffect } from 'react';
import Modal from '../Modal';
import { useApi } from '../../contexts/ApiContext';
import { useToast } from '../../contexts/ToastContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface SupportReason {
  id: number;
  name: string;
  is_active: boolean;
}

interface SupportResponsible {
  id: number;
  name: string;
  is_active: boolean;
}

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

export default function CreateTicketModal({ isOpen, onClose, onSuccess }: Props) {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [reasons, setReasons] = useState<SupportReason[]>([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [showNewReasonInput, setShowNewReasonInput] = useState(false);
  const [newReasonName, setNewReasonName] = useState('');
  const [creatingReason, setCreatingReason] = useState(false);
  const [responsibles, setResponsibles] = useState<SupportResponsible[]>([]);
  const [loadingResponsibles, setLoadingResponsibles] = useState(false);
  const [showNewResponsibleInput, setShowNewResponsibleInput] = useState(false);
  const [newResponsibleName, setNewResponsibleName] = useState('');
  const [creatingResponsible, setCreatingResponsible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    target_date: '',
    order_number: '',
    reason: '',
    responsible: 'Client',
    priority: 'Medium',
    description: '',
    image_data: null as string | null
  });

  // Cargar motivos y responsables desde la API
  useEffect(() => {
    if (isOpen) {
      loadReasons();
      loadResponsibles();
      setFormData({
        target_date: '',
        order_number: '',
        reason: '',
        responsible: '',
        priority: 'Medium',
        description: '',
        image_data: null
      });
      setShowNewReasonInput(false);
      setNewReasonName('');
      setShowNewResponsibleInput(false);
      setNewResponsibleName('');
    }
  }, [isOpen]);

  const loadReasons = async () => {
    try {
      setLoadingReasons(true);
      const response = await api.get('/support/reasons');
      const reasonsData = response.data || [];
      setReasons(reasonsData);
      // Si hay motivos y no hay uno seleccionado, seleccionar el primero
      if (reasonsData.length > 0) {
        setFormData(prev => {
          // Solo actualizar si no hay motivo seleccionado
          if (!prev.reason) {
            return { ...prev, reason: reasonsData[0].name };
          }
          return prev;
        });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Error desconocido';
      showError(`Error al cargar los motivos: ${errorMessage}`);
      // Aún así, intentar continuar con una lista vacía
      setReasons([]);
    } finally {
      setLoadingReasons(false);
    }
  };

  const loadResponsibles = async () => {
    try {
      setLoadingResponsibles(true);
      const response = await api.get('/support/responsibles');
      const responsiblesData = response.data || [];
      setResponsibles(responsiblesData);
      // Si hay responsables y no hay uno seleccionado, seleccionar el primero
      if (responsiblesData.length > 0) {
        setFormData(prev => {
          // Solo actualizar si no hay responsable seleccionado
          if (!prev.responsible) {
            return { ...prev, responsible: responsiblesData[0].name };
          }
          return prev;
        });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Error desconocido';
      showError(`Error al cargar los responsables: ${errorMessage}`);
      // Aún así, intentar continuar con una lista vacía
      setResponsibles([]);
    } finally {
      setLoadingResponsibles(false);
    }
  };

  const handleCreateReason = async () => {
    if (!newReasonName.trim()) {
      showError('Por favor ingresa un nombre para el motivo');
      return;
    }

    try {
      setCreatingReason(true);
      const response = await api.post('/support/reasons', { name: newReasonName.trim() });
      await loadReasons(); // Recargar la lista
      setFormData(prev => ({ ...prev, reason: response.data.name }));
      setShowNewReasonInput(false);
      setNewReasonName('');
      showSuccess('Motivo creado correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al crear el motivo');
    } finally {
      setCreatingReason(false);
    }
  };

  const handleCreateResponsible = async () => {
    if (!newResponsibleName.trim()) {
      showError('Por favor ingresa un nombre para el responsable');
      return;
    }

    try {
      setCreatingResponsible(true);
      const response = await api.post('/support/responsibles', { name: newResponsibleName.trim() });
      await loadResponsibles(); // Recargar la lista
      setFormData(prev => ({ ...prev, responsible: response.data.name }));
      setShowNewResponsibleInput(false);
      setNewResponsibleName('');
      showSuccess('Responsable creado correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al crear el responsable');
    } finally {
      setCreatingResponsible(false);
    }
  };

  const handleImageUpload = (file: File) => {
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
        showError('La imagen es demasiado grande (max 5MB)');
        return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, image_data: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const onPaste = (e: React.ClipboardEvent) => {
    if (e.clipboardData.files.length > 0) {
      handleImageUpload(e.clipboardData.files[0]);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      handleImageUpload(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!formData.target_date || !formData.description) {
      showError('Por favor rellena la fecha objetivo y la descripción');
      return;
    }
    if (!formData.reason) {
      showError('Por favor selecciona o crea un motivo');
      return;
    }
    if (!formData.responsible) {
      showError('Por favor selecciona o crea un responsable');
      return;
    }

    try {
      setLoading(true);
      await api.post('/support', formData);
      showSuccess('Ticket creado correctamente');
      onSuccess();
      onClose();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al crear ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nuevo Ticket de Soporte"
      footer={
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', width: '100%' }}>
           <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancelar</button>
           <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
             {loading ? 'Creando...' : 'Crear Ticket'}
           </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div className="form-group">
            <label>Fecha Objetivo</label>
            <input 
                type="date" 
                className="form-control"
                value={formData.target_date}
                onChange={e => setFormData({...formData, target_date: e.target.value})}
            />
        </div>
        
        <div className="form-group">
            <label>Número de Pedido (Shopify)</label>
            <input 
                type="text" 
                className="form-control"
                value={formData.order_number}
                onChange={e => setFormData({...formData, order_number: e.target.value})}
                placeholder="#1234"
            />
        </div>

        <div className="form-group">
            <label>Motivo</label>
            {loadingReasons ? (
              <div style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>Cargando motivos...</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <select 
                      className="form-control"
                      value={formData.reason}
                      onChange={e => {
                        if (e.target.value === '__new__') {
                          setShowNewReasonInput(true);
                          setFormData({...formData, reason: ''});
                        } else {
                          setFormData({...formData, reason: e.target.value});
                          setShowNewReasonInput(false);
                        }
                      }}
                      style={{ flex: 1 }}
                  >
                      <option value="">Selecciona un motivo</option>
                      {reasons.map(r => (
                        <option key={r.id} value={r.name}>{r.name}</option>
                      ))}
                      <option value="__new__">+ Agregar nuevo motivo</option>
                  </select>
                </div>
                {showNewReasonInput && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Nombre del nuevo motivo"
                      value={newReasonName}
                      onChange={e => setNewReasonName(e.target.value)}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          handleCreateReason();
                        }
                      }}
                      style={{ flex: 1 }}
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleCreateReason}
                      disabled={creatingReason || !newReasonName.trim()}
                    >
                      {creatingReason ? 'Creando...' : 'Crear'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setShowNewReasonInput(false);
                        setNewReasonName('');
                        if (reasons.length > 0) {
                          setFormData(prev => ({ ...prev, reason: reasons[0].name }));
                        }
                      }}
                      disabled={creatingReason}
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </>
            )}
        </div>

        <div className="form-group">
            <label>Responsable</label>
            {loadingResponsibles ? (
              <div style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>Cargando responsables...</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <select 
                      className="form-control"
                      value={formData.responsible}
                      onChange={e => {
                        if (e.target.value === '__new__') {
                          setShowNewResponsibleInput(true);
                          setFormData({...formData, responsible: ''});
                        } else {
                          setFormData({...formData, responsible: e.target.value});
                          setShowNewResponsibleInput(false);
                        }
                      }}
                      style={{ flex: 1 }}
                  >
                      <option value="">Selecciona un responsable</option>
                      {responsibles.map(r => (
                        <option key={r.id} value={r.name}>{r.name}</option>
                      ))}
                      <option value="__new__">+ Agregar nuevo responsable</option>
                  </select>
                </div>
                {showNewResponsibleInput && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Nombre del nuevo responsable"
                      value={newResponsibleName}
                      onChange={e => setNewResponsibleName(e.target.value)}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          handleCreateResponsible();
                        }
                      }}
                      style={{ flex: 1 }}
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleCreateResponsible}
                      disabled={creatingResponsible || !newResponsibleName.trim()}
                    >
                      {creatingResponsible ? 'Creando...' : 'Crear'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setShowNewResponsibleInput(false);
                        setNewResponsibleName('');
                        if (responsibles.length > 0) {
                          setFormData(prev => ({ ...prev, responsible: responsibles[0].name }));
                        }
                      }}
                      disabled={creatingResponsible}
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </>
            )}
        </div>

        <div className="form-group">
            <label>Prioridad</label>
            <select 
                className="form-control"
                value={formData.priority}
                onChange={e => setFormData({...formData, priority: e.target.value})}
            >
                {PRIORITIES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
        </div>

        <div className="form-group">
            <label>Descripción del Problema</label>
            <textarea
                className="form-control"
                rows={5}
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
                onPaste={onPaste}
                onDrop={onDrop}
                onDragOver={e => e.preventDefault()}
                placeholder="Describe el problema aquí. Puedes pegar imágenes o arrastrarlas."
            />
        </div>

        <div className="form-group">
            <label>Adjuntar Imagen (Opcional)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input 
                    type="file" 
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={e => e.target.files && handleImageUpload(e.target.files[0])}
                    style={{ display: 'none' }}
                />
                <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => fileInputRef.current?.click()}
                >
                    Seleccionar Imagen
                </button>
                {formData.image_data && <span style={{ color: 'var(--success-color)' }}>Imagen adjuntada ✓</span>}
            </div>
            {formData.image_data && (
                <div style={{ marginTop: '0.5rem' }}>
                    <img src={formData.image_data} alt="Preview" style={{ maxHeight: '100px', borderRadius: '4px' }} />
                    <button 
                        className="btn btn-danger btn-sm"
                        style={{ marginLeft: '0.5rem', padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                        onClick={() => setFormData({...formData, image_data: null})}
                    >
                        Quitar
                    </button>
                </div>
            )}
        </div>
      </div>
    </Modal>
  );
}

